/**
 * CurrencySelect — standardized ISO-4217 currency picker.
 *
 * Wraps shadcn/ui <Select> with:
 *   - Redux-driven supportedCurrencies list (auto-fetched once if empty)
 *   - A safe fallback list for offline / first-paint scenarios
 *   - Optional "Account default" sentinel (mapped internally to '' on change)
 *   - Consistent test ids across all forms
 *
 * Replaces 6 ad-hoc CURRENCY_OPTIONS arrays previously duplicated across the
 * codebase (Settings, Transaction, Template, Subscription, Account, Rule).
 */
import { useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchSupportedCurrencies } from '@/features/currency/currencySlice';
import { getCurrencySymbol } from '@/lib/utils';

const FALLBACK_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'IDR', 'SGD', 'AUD', 'CAD'];

/** Internal sentinel — shadcn/Radix Select cannot accept an empty-string item value. */
const DEFAULT_SENTINEL = '__default__';

export interface CurrencySelectProps {
  /** Empty string = use account default; otherwise an ISO-4217 code. */
  value: string;
  /** Emits '' for "account default", or a 3-letter ISO code otherwise. */
  onChange: (code: string) => void;
  /** When true, exposes an "Account default" option. Defaults to false. */
  allowAccountDefault?: boolean;
  /** Currency code label shown next to "Account default". Only used when `allowAccountDefault`. */
  accountCurrency?: string;
  /** When true, hides the "Account default" option even if `allowAccountDefault`. */
  required?: boolean;
  disabled?: boolean;
  /** id forwarded to <SelectTrigger> for <Label htmlFor>. */
  id?: string;
  /** Base test id; defaults to 'currency-select'. */
  testId?: string;
}

export function CurrencySelect({
  value,
  onChange,
  allowAccountDefault = false,
  accountCurrency,
  required = false,
  disabled = false,
  id,
  testId = 'currency-select',
}: CurrencySelectProps) {
  const dispatch = useAppDispatch();
  const supported = useAppSelector((s) => s.currency.supportedCurrencies);
  const dispatched = useRef(false);

  useEffect(() => {
    if (supported.length === 0 && !dispatched.current) {
      dispatched.current = true;
      dispatch(fetchSupportedCurrencies());
    }
  }, [supported.length, dispatch]);

  const list = supported.length > 0 ? supported : FALLBACK_CURRENCIES;
  const showDefault = allowAccountDefault && !required;
  const selectValue = value === '' ? DEFAULT_SENTINEL : value;

  return (
    <Select
      value={selectValue}
      onValueChange={(v) => onChange(v === DEFAULT_SENTINEL ? '' : v)}
      disabled={disabled}
    >
      <SelectTrigger id={id} data-testid={`${testId}-trigger`} className="min-h-[44px]">
        <SelectValue placeholder={showDefault ? 'Account default' : 'Select currency'} />
      </SelectTrigger>
      <SelectContent>
        {showDefault && (
          <SelectItem value={DEFAULT_SENTINEL} data-testid={`${testId}-default`}>
            Account default{accountCurrency ? ` (${accountCurrency})` : ''}
          </SelectItem>
        )}
        {list.map((code) => (
          <SelectItem key={code} value={code} data-testid={`${testId}-option-${code}`}>
            {code} — {getCurrencySymbol(code)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default CurrencySelect;
